import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
  Chip,
  Modal,
  TextField,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
  useMediaQuery,
  Theme,
  Paper,
  Checkbox
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  fetchServices,
  addService,
  updateService,
  deleteService
} from 'slices/NdisSlices/platformAdmin/ManageServicesSlice';
import { Service, Role } from 'types/ndisSection/manageServicesTypes';
import { RootState } from 'app/store';
import Toast from 'commonComponents/Toast';
import { showToast } from 'store/slices/toastSlice';
import { fetchRoles } from 'slices/NdisSlices/platformAdmin/ManageRolesSlice';

const ManageServices: React.FC = () => {
  const dispatch = useDispatch();
  const { services, loading, error } = useSelector((state: RootState) => state.manageServices);
  const { roles } = useSelector((state: RootState) => state.manageRoles);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // Modal state
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({
    service_id: '',
    header: '',
    description: '',
    shortDescription: '',
    roles: [], // Default to empty array to avoid undefined
    background: '#4e79a7',
    route: '',
    icon: '',
    PrimaryRender: false,
    owner: [],
    created_by: '',
    created_at: ''
  });
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(fetchServices());
    dispatch(fetchRoles());
  }, [dispatch]);

  const showNotification = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    dispatch(showToast({ message, severity }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.header) newErrors.header = 'Header is required';
    if (!formData.shortDescription) newErrors.shortDescription = 'Short description is required';
    if (!formData.route) newErrors.route = 'Route is required';
    if (!formData.roles?.length) newErrors.roles = 'At least one role is required';
    if (!formData.icon && !iconFile) newErrors.icon = 'Icon is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({
      service_id: '',
      header: '',
      description: '',
      shortDescription: '',
      roles: [],
      background: '#4e79a7',
      route: '',
      icon: '',
      PrimaryRender: false,
      owner: [],
      created_by: '',
      created_at: ''
    });
    setIconFile(null);
    setIconPreview(null);
    setErrors({});
    setOpen(true);
  };

  const handleOpenEdit = (service: Service) => {
    setIsEditMode(true);
    setSelectedService(service);
    setFormData(service);
    setIconFile(null);
    setIconPreview(service.icon || null);
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedService(null);
    setFormData({
      service_id: '',
      header: '',
      description: '',
      shortDescription: '',
      roles: [],
      background: '#4e79a7',
      route: '',
      icon: '',
      PrimaryRender: false,
      owner: [],
      created_by: '',
      created_at: ''
    });
    setIconFile(null);
    setIconPreview(null);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      let updatedFormData = { ...formData };
      if (iconFile) {
        const reader = new FileReader();
        reader.readAsDataURL(iconFile);
        await new Promise((resolve) => {
          reader.onload = () => {
            updatedFormData = { ...updatedFormData, icon: reader.result as string };
            resolve(null);
          };
        });
      }

      if (isEditMode && selectedService) {
        await dispatch(updateService(updatedFormData as Service));
        showNotification('Service updated successfully!', 'success');
      } else {
        await dispatch(addService(updatedFormData as Service));
        showNotification('Service added successfully!', 'success');
      }
      handleClose();
    } catch (err) {
      showNotification('Failed to save service. Please try again.', 'error');
    }
  };

  const handleDelete = async (service_id: string) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await dispatch(deleteService(service_id));
        showNotification('Service deleted successfully!', 'success');
      } catch (err) {
        showNotification('Failed to delete service. Please try again.', 'error');
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    // Handle both string (single select) and string[] (multiple select)
    const rolesArray = typeof value === 'string' ? [value] : value;
    setFormData((prev) => ({ ...prev, roles: rolesArray }));
    if (errors.roles) setErrors((prev) => ({ ...prev, roles: '' }));
  };

  const handleSelectAllRoles = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const allRoleIds = roles.map((role) => role.role_id);
    const newRoles = e.target.checked ? allRoleIds : [];
    setFormData((prev) => ({ ...prev, roles: newRoles }));
    if (errors.roles) setErrors((prev) => ({ ...prev, roles: '' }));
  };

 

  const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setIconFile(file);
    if (file) {
      setIconPreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, icon: '' }));
    } else {
      setIconPreview(null);
    }
  };

  const handlePrimaryRenderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, PrimaryRender: e.target.checked }));
  };

  // Map role_id to label for display
  const getRoleLabel = (role_id: string) => {
    const role = roles.find((r) => r.role_id === role_id);
    return role ? role.label : role_id;
  };

  return (
    <Box sx={{ p: isMobile ? 0 : 0, bgcolor: 'background.paper' }}>
      <Paper
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)'
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={isMobile ? 'column' : 'row'}
          gap={2}
        >
          <Typography variant="h4" fontWeight="bold" color="primary">
            Manage Services
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAdd}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
              }
            }}
          >
            Add Service
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : error ? (
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light' }}>
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Paper>
      ) : services.length > 0 ? (
        <Grid container spacing={isMobile ? 2 : 3}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} md={4} key={service.service_id}>
              <motion.div>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    borderLeft: `4px solid ${service.background}`,
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: service.background,
                          mr: 2,
                          width: 48,
                          height: 48,
                          fontSize: '1.5rem'
                        }}
                        src={service.icon}
                      />
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {service.header}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {service.shortDescription}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                      {service.roles.map((role_id) => (
                        <Chip
                          key={role_id}
                          label={getRoleLabel(role_id)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ borderRadius: 4 }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Created by: {service.created_by}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Created at: {new Date(service.created_at).toLocaleString()}
                    </Typography>
                  </CardContent>
                  <Divider />
                  <Box
                    p={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    bgcolor="action.hover"
                  >
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={service.PrimaryRender || false}
                            color="primary"
                            size="small"
                            sx={{
                              ml: 1,
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#1976d2'
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#1976d2'
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: '#bdbdbd'
                              },
                              '& .MuiSwitch-thumb': {
                                boxShadow: '0 0 2px black'
                              }
                            }}
                          />
                        }
                        label="Primary"
                        labelPlacement="start"
                        sx={{ m: 0 }}
                      />
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => (window.location.href = service.route)}
                        sx={{ borderRadius: 1 }}
                      >
                        View
                      </Button>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenEdit(service)}
                        size="small"
                        sx={{ border: '1px solid', borderRadius: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(service.service_id)}
                        size="small"
                        sx={{ border: '1px solid', borderRadius: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No services available. Create your first service!
          </Typography>
        </Paper>
      )}

      {/* Modal for Add/Edit */}
      <Modal open={open} onClose={handleClose}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : 600,
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: 24,
            p: 3,
            borderRadius: 3
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" color="primary">
              {isEditMode ? 'Edit Service' : 'Add New Service'}
            </Typography>
            <IconButton onClick={handleClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Header *"
                name="header"
                value={formData.header || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                error={!!errors.header}
                helperText={errors.header}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  label="Icon"
                  type="file"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: 'image/*' }}
                  onChange={handleIconFileChange}
                  fullWidth
                  margin="normal"
                  error={!!errors.icon}
                  helperText={errors.icon}
                  variant="outlined"
                  size="small"
                />
                {iconPreview && (
                  <Avatar
                    src={iconPreview}
                    sx={{
                      width: 40,
                      height: 40,
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          <TextField
            label="Short Description *"
            name="shortDescription"
            value={formData.shortDescription || ''}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            error={!!errors.shortDescription}
            helperText={errors.shortDescription}
            variant="outlined"
            size="small"
            multiline
            rows={2}
          />

          <TextField
            label="Description"
            name="description"
            value={formData.description || ''}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            variant="outlined"
            size="small"
            multiline
            rows={3}
          />

          <Box mt={2} mb={1}>
            <Typography variant="subtitle1" gutterBottom>
              Roles *
            </Typography>
            <FormControl fullWidth margin="normal" size="small" error={!!errors.roles}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={formData.roles || []}
                onChange={handleRoleChange}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((role_id) => (
                      <Chip key={role_id} label={getRoleLabel(role_id)} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem dense sx={{ pl: 1 }}>
                  <Checkbox
                    size="small"
                    checked={(formData.roles || []).length === roles.length}
                    indeterminate={
                      (formData.roles || []).length > 0 &&
                      (formData.roles || []).length < roles.length
                    }
                    onChange={handleSelectAllRoles}
                  />
                  <Typography variant="body2">Select All</Typography>
                </MenuItem>
                <Divider />
                {roles.map((role) => (
                  <MenuItem key={role.role_id} value={role.role_id} dense>
                    <Checkbox
                      size="small"
                      checked={(formData.roles || []).includes(role.role_id)}
                    />
                    <Typography variant="body2">{role.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {errors.roles && (
                <Typography color="error" variant="caption">
                  {errors.roles}
                </Typography>
              )}
            </FormControl>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Background Color"
                name="background"
                value={formData.background || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: formData.background,
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        mr: 1
                      }}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Route *"
                name="route"
                value={formData.route || ''}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                error={!!errors.route}
                helperText={errors.route}
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={formData.PrimaryRender || false}
                onChange={handlePrimaryRenderChange}
                color="primary"
                sx={{ ml: 1 }}
              />
            }
            label="Primary Render"
            sx={{ mt: 2, display: 'flex', alignItems: 'center' }}
          />

          <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: 2, px: 3, py: 1 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={
                !formData.header ||
                !formData.shortDescription ||
                !formData.route ||
                !(formData.roles ?? []).length ||
                (!formData.icon && !iconFile)
              }
              sx={{ borderRadius: 2, px: 3, py: 1 }}
            >
              {isEditMode ? 'Update Service' : 'Add Service'}
            </Button>
          </Box>
        </Paper>
      </Modal>

      <Toast />
    </Box>
  );
};

export default ManageServices;
